import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import ReactPaginate from "react-paginate";
import { format } from "date-fns";
import { ProductContext } from "../../../../store";
import ReportHeader from "../../../../components/Search/ReportHeader";
import ReportSortArrows from "../../../../components/Search/ReportSortArrows";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";
import TableSkeleton from "../../../../components/Skeleton/TableSkeleton";
import { PostData } from "../../../../services/FetchData";
import { searchReportsAnalyticsUrl } from "../../../../constants/searchReportsAnalyticsApi";
import { toast } from "react-toastify";

const KeywordProducts = () => {
  const [pageLoader, setPageLoader] = useState(true);
  const [tableLoader, setTableLoader] = useState(true);
  const [keywords, setKeywords] = useState([]);
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [expandedKeywords, setExpandedKeywords] = useState({});
  const [sortBy, setSortBy] = useState("conversions");
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" | "desc"

  const { dateRange, setDateRange, currentWebsiteId = 1 } = useContext(ProductContext);

  useEffect(() => {
    if (currentWebsiteId) {
      // Reset expanded states when page or limit changes
      setExpandedKeywords({});
      getReports();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limit, page, currentWebsiteId, dateRange, sortBy, sortOrder]);

  const setSort = (field, order, e) => {
    e?.stopPropagation?.();
    setPage(0);
    setSortBy(field);
    setSortOrder(order);
  };

  const toggleSort = (field, e) => {
    e?.stopPropagation?.();
    setPage(0);
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const getReports = () => {
    setTableLoader(true);
    const payload = {
      websiteId: currentWebsiteId,
      startDate: format(dateRange.startDate, "yyyy-MM-dd"),
      endDate: format(dateRange.endDate, "yyyy-MM-dd"),
      page: page + 1,
      limit,
      sortBy,
      sortOrder,
    };

    PostData("ms1", searchReportsAnalyticsUrl("searchKeyProductPerformanceGrouped"), payload)
      .then((response) => {
        if (response !== "Invalid" && response !== "undefined") {
          if (response?.status === "success") {
            // V2 API returns data.data (rows), data.totalPages
            const raw = response?.data?.data || [];
            const normalized = Array.isArray(raw) ? raw.map((k) => ({ ...k, products: Array.isArray(k?.products) ? k.products : [] })) : [];
            setKeywords(normalized);
            setTotalPages(response?.data?.totalPages || 0);
          } else {
            toast.error(response?.message || "Failed to fetch keyword products data");
          }
        }
        setTableLoader(false);
        setPageLoader(false);
      })
      .catch((err) => {
        console.error("Error fetching keyword products:", err);
        toast.error("Error fetching keyword products data");
        setTableLoader(false);
        setPageLoader(false);
      });
  };

  const getKeywordId = (keyword, keywordIndex) => {
    const raw = String(keyword?.searchKey || keyword?.keyword || keyword?.key || "").trim();
    return raw !== "" ? raw : `keyword_${keywordIndex}`;
  };

  const toggleKeywordExpansion = (keywordId) => {
    setExpandedKeywords((prev) => ({
      ...prev,
      [keywordId]: !prev[keywordId],
    }));
  };

  const paperPlane = useMemo(() => require("./../../../../assets/images/paper-plane-grey.png"), []);

  const formatCurrency = (value) => {
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n)) {
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return "$0.00";
  };

  const safeInt = (value) => (typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0);

  // Keep rate calculations aligned with ProductKeywords.jsx
  // CTR% as requested: conversions / impressions (or conversions / searchVolume when impressions not available)
  const calculateTotalCTR = (impressions, conversions) => {
    const imp = safeInt(impressions);
    const conv = safeInt(conversions);
    return imp > 0 ? ((conv / imp) * 100).toFixed(2) : "0.00";
  };

  const calculateTotalConversionRate = (impressions, conversions) => {
    const imp = safeInt(impressions);
    const conv = safeInt(conversions);
    return imp > 0 ? ((conv / imp) * 100).toFixed(2) : "0.00";
  };

  return (
    <div>
      {pageLoader ? (
        <DetailSkeleton />
      ) : (
        <React.Fragment>
          <ReportHeader range={dateRange} onDateChange={(range) => setDateRange(range)} reportKey="keywordProducts" sortBy={sortBy} sortOrder={sortOrder} />
          <div className="tab-content mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Keyword Product Performance</h4>
              <div className="d-flex align-items-center">
                <label htmlFor="limit" className="me-2">
                  Items Per Page:
                </label>
                <select
                  id="limit"
                  className="form-select"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(0);
                  }}
                  style={{ width: "100px", fontSize: "12px" }}
                >
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div>
              {tableLoader ? (
                <TableSkeleton />
              ) : (
                <Table className={`table ${keywords.length > 0 ? "table-striped" : ""} mt-3 custom-bordered-table`}>
                  <thead>
                    <tr>
                      <th style={{ width: "30px" }}></th>
                      <th>Search Keyword</th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("searchVolume", e)}>
                        <div className="sortable-header-inner">
                          Search Volume
                          <ReportSortArrows field="searchVolume" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("clicks", e)}>
                        <div className="sortable-header-inner">
                          Clicks
                          <ReportSortArrows field="clicks" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("addToCarts", e)}>
                        <div className="sortable-header-inner">
                          Add-to-Cart
                          <ReportSortArrows field="addToCarts" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("conversions", e)}>
                        <div className="sortable-header-inner">
                          Conversions
                          <ReportSortArrows field="conversions" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("revenue", e)}>
                        <div className="sortable-header-inner">
                          Revenue
                          <ReportSortArrows field="revenue" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("ctr", e)}>
                        <div className="sortable-header-inner">
                          CTR %
                          <ReportSortArrows field="ctr" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                      <th className="sortable-header" style={{ cursor: "pointer" }} onClick={(e) => toggleSort("conversionRate", e)}>
                        <div className="sortable-header-inner">
                          Conversion Rate %
                          <ReportSortArrows field="conversionRate" activeSortField={sortBy} sortOrder={sortOrder} onSort={setSort} />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.length === 0 ? (
                      <tr>
                        <td colSpan={9}>
                          <div className="emptyPlaceholder">
                            <div>
                              <div>
                                <img src={paperPlane} className="img-fluid" alt="No data found" />
                              </div>
                              <div className="rsInfoMsg disabled">
                                <div className="flex-1">No records found.</div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      keywords.map((keyword, keywordIndex) => {
                        const keywordId = getKeywordId(keyword, keywordIndex);
                        const isExpanded = !!expandedKeywords[keywordId];
                        const products = keyword?.products || [];
                        const searchVolume = safeInt(keyword?.searchVolume);
                        const clicks = safeInt(keyword?.clicks);
                        const addToCarts =
                          keyword?.addToCarts !== undefined && keyword?.addToCarts !== null
                            ? safeInt(keyword?.addToCarts)
                            : keyword?.addToCart !== undefined && keyword?.addToCart !== null
                              ? safeInt(keyword?.addToCart)
                              : products.reduce(
                                  (acc, p) => acc + safeInt(p?.addToCarts ?? p?.addToCart),
                                  0
                                );
                        const conversions = safeInt(keyword?.conversions);
                        const ctr = calculateTotalCTR(searchVolume, conversions);
                        const conversionRate = calculateTotalConversionRate(searchVolume, conversions);

                        return (
                          <React.Fragment key={keywordId}>
                            <tr style={{ cursor: "pointer" }} onClick={() => toggleKeywordExpansion(keywordId)}>
                              <td>
                                <i
                                  className={`fas fa-chevron-${isExpanded ? "down" : "right"}`}
                                  style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    transition: "transform 0.2s",
                                  }}
                                />
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: "4px 8px",
                                    backgroundColor: "#e3f2fd",
                                    borderRadius: "4px",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {keyword?.searchKey ?? ""}
                                </span>
                              </td>
                              <td>{searchVolume.toLocaleString()}</td>
                              <td>{clicks.toLocaleString()}</td>
                              <td>{addToCarts.toLocaleString()}</td>
                              <td>{conversions.toLocaleString()}</td>
                              <td>{formatCurrency(keyword?.revenue)}</td>
                              <td>{ctr}%</td>
                              <td>{conversionRate}%</td>
                            </tr>

                            {isExpanded && (
                              <tr>
                                <td colSpan={9} style={{ padding: 0, backgroundColor: "#f8f9fa" }}>
                                  <div style={{ padding: "15px", marginLeft: "30px" }}>
                                    {products.length === 0 ? (
                                      <div className="text-muted small">No product data for this keyword.</div>
                                    ) : (
                                    <Table className="table table-sm mb-0" style={{ backgroundColor: "white" }}>
                                      <thead>
                                        <tr>
                                          <th>Product</th>
                                          <th>Impressions</th>
                                          <th>Clicks</th>
                                          <th>Add-to-Cart</th>
                                          <th>Conversions</th>
                                          <th>Revenue</th>
                                          <th>CTR %</th>
                                          <th>Conversion Rate %</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {products.map((product, productIndex) => (
                                          <tr key={product?.productId || `${keywordId}_product_${productIndex}`}>
                                            <td>
                                              <div className="d-flex align-items-center">
                                                <img
                                                  src={product?.productImage || paperPlane}
                                                  alt={product?.productName || "Product"}
                                                  width="35"
                                                  height="35"
                                                  style={{
                                                    objectFit: "cover",
                                                    marginRight: "8px",
                                                    borderRadius: "4px",
                                                  }}
                                                  onError={(e) => {
                                                    e.target.src = paperPlane;
                                                  }}
                                                />
                                                <span>{product?.productName || "N/A"}</span>
                                              </div>
                                            </td>
                                            <td>{safeInt(product?.impressions).toLocaleString()}</td>
                                            <td>{safeInt(product?.clicks).toLocaleString()}</td>
                                            <td>{safeInt(product?.addToCarts ?? product?.addToCart).toLocaleString()}</td>
                                            <td>{safeInt(product?.conversions).toLocaleString()}</td>
                                            <td>{formatCurrency(product?.revenue)}</td>
                                            <td>{calculateTotalCTR(product?.impressions, product?.conversions)}%</td>
                                            <td>{calculateTotalConversionRate(product?.impressions, product?.conversions)}%</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </Table>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              )}

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <ReactPaginate
                    nextLabel=">"
                    onPageChange={(event) => setPage(event.selected)}
                    page={page}
                    pageRangeDisplayed={3}
                    marginPagesDisplayed={2}
                    pageCount={totalPages}
                    previousLabel="<"
                    pageClassName="page-item"
                    pageLinkClassName="page-link"
                    previousClassName="page-item"
                    previousLinkClassName="page-link"
                    nextClassName="page-item"
                    nextLinkClassName="page-link"
                    breakLabel="..."
                    breakClassName="page-item"
                    breakLinkClassName="page-link"
                    containerClassName="pagination"
                    activeClassName="active"
                    renderOnZeroPageCount={null}
                    forcePage={page}
                  />
                </div>
              )}
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default KeywordProducts;
